import { fromJS } from 'immutable';
import isEmpty from 'lodash/isEmpty';
import cloneDeep from 'lodash/cloneDeep';

import { boundariesLayer, choroplethBoundariesLayer, createBoundaries } from './layers/boundaries';
import { clustersLayer, clustersCountLayer } from './layers/clusters';


export const datapointsLayer = (color, index, source) => ({
  id: `point${color}${index}`,
  source,
  type: 'circle',
  paint: {
    'circle-radius': 5,
    'circle-stroke-width': 1,
    'circle-stroke-color': '#fff',
    'circle-color': color,
  },
});

export const setMapDatasets = (mapStyle, data, color) => {
  const defaultMapStyle = fromJS(mapStyle);

  return defaultMapStyle
    .setIn(['sources', 'datasets'], fromJS({ data, type: 'geojson' }))
    .set('layers', defaultMapStyle.get('layers')
      .push(datapointsLayer(color, 1, 'datasets'))
      .push(clustersLayer(color, 1, 'datasets'))
      .push(clustersCountLayer(color, 1, 'datasets')));
};

export const extendDefaultMapWith = (
  defaultMapStyle,
  dataMap,
  {
    lineColor,
    lineWidth,
    visualizationType,
    choroplethAggregate,
    heatmapGradient,
    mapShapes,
  },
) => {
  const defaultMapStyleClone = cloneDeep(defaultMapStyle);

  const [mapDatasets, boundariesShapes] = dataMap;
  const choroplethValues = (choroplethAggregate && choroplethAggregate.totals && choroplethAggregate.totals.mapShapes) || [];
  const choroplethValuesByMapShapeId = choroplethValues.reduce((accumulator, shapeTotal) => ({
    ...accumulator,
    [String(shapeTotal.id)]: shapeTotal.value,
  }), {});
  const choroplethOnlyValues = choroplethValues.map(({ value }) => value).filter(value => value !== null && value !== undefined);
  const boundaries = createBoundaries(boundariesShapes || [], {
    mapShapes,
    choroplethValuesByMapShapeId,
  });

  const mapPoints = mapDatasets.map(datapoint => datapoint.datapointsGeojson);

  defaultMapStyleClone.sources.boundaries = {
    data: boundaries,
    type: 'geojson',
  };

  if (!isEmpty(boundaries.features)) {
    if (visualizationType === 'CHOROPLETH') {
      defaultMapStyleClone.layers.push(choroplethBoundariesLayer(heatmapGradient, {
        min: Math.min(...choroplethOnlyValues),
        max: Math.max(...choroplethOnlyValues),
      }));
    }

    defaultMapStyleClone.layers.push(boundariesLayer(lineColor, lineWidth));
  }

  const extendedMapStyles = mapPoints.reduce((mapStylesAccumulator, datapoints, index) => {
    const { color, clustered } = mapDatasets[index];
    const source = `datapoints-${index}`;

    /* eslint no-param-reassign: "error" */
    mapStylesAccumulator.sources[source] = {
      data: datapoints,
      type: 'geojson',
      cluster: clustered,
      clusterMaxZoom: 14,
    };

    if (visualizationType !== 'HEATMAP' && visualizationType !== 'CHOROPLETH') {
      mapStylesAccumulator.layers.push(datapointsLayer(color, index, source));
      mapStylesAccumulator.layers.push(clustersLayer(color, index, source));
      mapStylesAccumulator.layers.push(clustersCountLayer(color, index, source));
    }

    return mapStylesAccumulator;
  }, defaultMapStyleClone);

  return fromJS(extendedMapStyles);
};

// eslint-disable-next-line
export const setEditMapData = async (mapStyle, data, options) => extendDefaultMapWith(mapStyle, data, options);
